import * as React from 'react';

import type { DragEndEvent } from '@dnd-kit/core';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { enumUnit, Unit } from '@rematter/frontend-lib-api-client';
import { enumLoadWeightType } from '@rematter/modules/trade/types/load';
import { zeroWeight } from '@rematter/modules/trade/utils/weight';
import {
  Button,
  cn,
  Col,
  FloatingToolbar,
  IconButton,
  Row,
  SearchField,
  TouchButton,
  Widget,
} from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucideCheck, LucideGripVertical, LucideX } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

import { InventoryItemButton } from '../../_shared/InventoryItemButton';
import { NormalizedLineItem } from '../../_shared/ItemsInterface/_types';

import { $inventoryItems } from '~/modules/trade/_state/loadForm.state';
import {
  $quickAddItemsEditMode,
  $saveQuickAddItems,
  $scaleTicketQuickAddItemIds,
} from '~/modules/trade/_state/scaleTicketSettings.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';

type QuickAddMaterialButtonsProps = {
  onAddItem: (type: 'Raw', data: Partial<NormalizedLineItem>) => void;
  onFocusNewItem?: (itemId: string) => void;
  maxItems?: number;
};

//
// ⚛️ ⚛️ ⚛️ SORTABLE ITEM ROW ⚛️ ⚛️ ⚛️
//
type SortableItemRowProps = {
  id: string;
  name: string;
  onRemove: () => void;
};

const SortableItemRow = ({ id, name, onRemove }: SortableItemRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="@SortableItemRow flex items-center gap-1.5 rounded-md border border-foreground/10 bg-neutral/5 px-2 py-1.5"
    >
      <span
        {...attributes}
        {...listeners}
        className="cursor-grab text-foreground/30"
      >
        <LucideGripVertical size={14} />
      </span>
      <span className="flex-1 truncate text-sm">{name}</span>
      <IconButton
        color="neutral"
        className="!h-auto !min-h-0 !w-auto !min-w-0 p-1"
        onPress={onRemove}
        icon={<LucideX size={12} />}
      />
    </div>
  );
};

//
// ⚛️ ⚛️ ⚛️ EDIT MODE VIEW ⚛️ ⚛️ ⚛️
//
const EditModeView = () => {
  const t = useTranslations();

  // States
  const [search, setSearch] = React.useState('');

  // Atoms
  const { data: inventoryItemsData } = useAtomValue($inventoryItems);
  const [selectedIds, setSelectedIds] = useAtom($scaleTicketQuickAddItemIds);
  const setEditMode = useSetAtom($quickAddItemsEditMode);
  const saveItems = useSetAtom($saveQuickAddItems);

  const allItems = inventoryItemsData?.allItems ?? [];

  // Data hooks
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 5 },
    }),
    useSensor(KeyboardSensor),
  );

  // Memoized values
  const searchResults = React.useMemo(() => {
    if (!search.trim()) return [];
    const term = search.toLowerCase();
    return allItems
      .filter(
        (item) =>
          !selectedIds.includes(item.id) &&
          (item.name.toLowerCase().includes(term) ||
            item.mnemonic?.toLowerCase().includes(term)),
      )
      .slice(0, 8);
  }, [allItems, search, selectedIds]);

  const selectedItems = React.useMemo(() => {
    const itemMap = new Map(allItems.map((item) => [item.id, item]));
    return selectedIds.map((id) => itemMap.get(id)).filter(Boolean);
  }, [selectedIds, allItems]);

  // Callbacks
  const handleDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = selectedIds.indexOf(String(active.id));
      const newIndex = selectedIds.indexOf(String(over.id));
      setSelectedIds(arrayMove(selectedIds, oldIndex, newIndex));
    },
    [selectedIds, setSelectedIds],
  );

  const handleAdd = React.useCallback(
    (id: string) => {
      setSelectedIds([...selectedIds, id]);
      setSearch('');
    },
    [selectedIds, setSelectedIds],
  );

  const handleRemove = React.useCallback(
    (id: string) => {
      setSelectedIds(selectedIds.filter((i) => i !== id));
    },
    [selectedIds, setSelectedIds],
  );

  return (
    <>
      <Widget className="@QuickAddMaterialButtonsEdit min-h-0 flex-1 p-2">
        <Col className="h-full">
          <span className="flex-shrink-0 pb-1.5 text-xs font-medium text-neutral/60 uppercase">
            {t.scaleTicketPro.settings.quickAddItemsTitle}
          </span>
          <div className="relative flex-shrink-0">
            <SearchField
              value={search}
              onChange={setSearch}
              placeholder={t.scaleTicketPro.settings.quickAddItemsSearch}
              aria-label={t.scaleTicketPro.settings.quickAddItemsSearch}
              className="text-xs"
            />
            {searchResults.length > 0 && (
              <Col
                gap1
                className="absolute top-full right-0 left-0 z-99 mt-1 rounded-md border border-foreground/10 bg-white p-1 shadow-md"
              >
                {searchResults.map((item) => (
                  <Button
                    key={item.id}
                    variant="outline"
                    color="primary"
                    className="rounded px-2 py-1 text-sm"
                    onPress={() => handleAdd(item.id)}
                  >
                    <Col yStart xStart className="w-full">
                      <span className="w-full truncate text-left">
                        {item.name}
                      </span>
                      <span className="text-xs text-foreground/50">
                        {item.mnemonic}
                      </span>
                    </Col>
                  </Button>
                ))}
              </Col>
            )}
            {search.trim() && searchResults.length === 0 && (
              <Col
                gap1
                className="absolute top-full right-0 left-0 z-99 mt-1 rounded-md border border-foreground/10 bg-white p-1 shadow-md"
              >
                <span className="block py-1 text-center text-xs text-foreground/60">
                  {t.scaleTicketPro.noSearchResults}
                </span>
              </Col>
            )}
          </div>
          <Col gap1 className="mt-1 flex-1 overflow-y-auto">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={selectedIds}
                strategy={verticalListSortingStrategy}
              >
                {selectedItems.map((item) =>
                  item ? (
                    <SortableItemRow
                      key={item.id}
                      id={item.id}
                      name={item.name}
                      onRemove={() => handleRemove(item.id)}
                    />
                  ) : null,
                )}
              </SortableContext>
            </DndContext>
            {selectedItems.length === 0 && !search.trim() && (
              <span className="py-2 text-center text-xs text-foreground/40">
                {t.scaleTicketPro.settings.quickAddItemsEmpty}
              </span>
            )}
          </Col>
        </Col>
      </Widget>
      <FloatingToolbar visible className="@QuickAddItemsEditToolbar">
        <Row gap3 yCenter>
          <span className="text-sm">
            {t.scaleTicketPro.settings.quickAddItemsDescription}
          </span>
          <TouchButton
            variant="outline"
            size="sm"
            onPress={() => setEditMode(false)}
          >
            {t.common.cancel}
          </TouchButton>
          <TouchButton color="primary" size="sm" onPress={() => saveItems()}>
            <Row gap1 yCenter>
              <LucideCheck size={14} />
              {t.common.save}
            </Row>
          </TouchButton>
        </Row>
      </FloatingToolbar>
    </>
  );
};

//
// ⚛️ ⚛️ ⚛️ QUICK ADD MATERIAL BUTTONS ⚛️ ⚛️ ⚛️
//
export const QuickAddMaterialButtons: React.FC<
  QuickAddMaterialButtonsProps
> = ({ onAddItem, onFocusNewItem, maxItems = 5 }) => {
  const t = useTranslations();

  // Atoms
  const editMode = useAtomValue($quickAddItemsEditMode);
  const { data: inventoryItemsData } = useAtomValue($inventoryItems);
  const configuredItemIds = useAtomValue($scaleTicketQuickAddItemIds);

  // Memoized values
  const quickAddItems = React.useMemo(() => {
    if (!inventoryItemsData?.allItems) return [];
    const allItems = inventoryItemsData.allItems;

    if (configuredItemIds.length > 0) {
      const itemMap = new Map(allItems.map((item) => [item.id, item]));
      return configuredItemIds
        .map((id) => itemMap.get(id))
        .filter(Boolean) as typeof allItems;
    }

    return allItems.slice(0, maxItems);
  }, [inventoryItemsData, configuredItemIds, maxItems]);

  // Callbacks
  const handleQuickAdd = React.useCallback(
    (item: { id: string; effectivePricingUnit?: string }) => {
      const newItemId = `temp-${uuidv4()}`;
      const unit = (item.effectivePricingUnit as Unit) ?? enumUnit.Pounds;
      onAddItem('Raw', {
        id: newItemId,
        itemId: item.id,
        weights: {
          [enumLoadWeightType.Gross]: zeroWeight(),
          [enumLoadWeightType.Tare]: zeroWeight(),
          [enumLoadWeightType.Net]: zeroWeight(),
        },
        pricingUnit: unit,
      });
      onFocusNewItem?.(newItemId);
    },
    [onAddItem, onFocusNewItem],
  );

  if (editMode) return <EditModeView />;

  if (quickAddItems.length === 0) {
    if (configuredItemIds.length > 0) return null;
    return (
      <Widget className="@QuickAddMaterialButtons min-h-0 flex-1 p-2">
        <Col xCenter yCenter className="h-full">
          <span className="text-xs text-neutral/40">
            {t.scaleTicketPro.settings.editQuickAddItems}
          </span>
        </Col>
      </Widget>
    );
  }

  return (
    <Widget className="@QuickAddMaterialButtons min-h-0 flex-1 p-2">
      <Col className="h-full">
        <span className="flex-shrink-0 pb-1.5 text-xs font-medium text-neutral/60 uppercase">
          {t.scaleTicketPro.actions.quickAdd}
        </span>
        <Col gap={1.5} className="flex-1 overflow-y-auto">
          {quickAddItems.map((item) => (
            <InventoryItemButton
              key={item.id}
              inventoryItem={item}
              isSelected={false}
              align="left"
              size="lg"
              className={cn(
                'flex w-full flex-shrink-0 items-center justify-center gap-2 !rounded-md',
                'truncate !border-none !bg-neutral/5 p-1 !text-sm !text-neutral !outline-none [&>*]:font-normal',
              )}
              onPress={() => handleQuickAdd(item)}
            />
          ))}
        </Col>
      </Col>
    </Widget>
  );
};
